import { eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@aula/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ───────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can
 * match **ZERO rows and return "success"**. The mechanism: when the CRUD
 * queue empties, PowerSync **rebuilds the local view from the last
 * checkpoint**. If the checkpoint does not yet contain the newly created row,
 * the row is **MISSING at that instant** — and an UPDATE against it writes
 * nothing, generates no op, and **does not fail**.
 *
 * First measured in `space-academy-app` (same domain: course→unit→lesson→
 * block, local-stack `activity` table, course `06a131d0`): the course is
 * created, 1.03 s later the unit is created, and **in between NO `course/updated`
 * op exists** — the title the author had just written never generated a CRUD
 * operation. The author renamed a newly created course, the rename was
 * silently lost, and **the student's certificate was sealed with the wrong
 * course name**.
 *
 * Why the absence of an error proves nothing here: the server's write path
 * intentionally **always returns 2xx** (a 4xx would block the client queue
 * forever). That case is a DIFFERENT path — there the server rejects and stays
 * silent — but the criterion is the same: a user write that disappears without
 * a trace is the worst failure mode.
 *
 * ⚠️ The window is NOT "X ms elapsed" — it is "the queue emptied midway". It
 * cannot be read afterwards in `activity`; it can only be closed by verifying
 * at write time.
 *
 * This module's contract: write, **READ BACK**, and compare. If the value did
 * not land, retry with backoff; if it still has not landed after exhaustion,
 * **THROW** — the caller reports it instead of leaving the user looking at a
 * field that represents nothing saved.
 *
 * Converged with canonical `listto-app@9ae347b6` (same module, same API) and
 * `space-academy-app@16bc571f` (same courses domain). This repo also does not
 * have compound mutations inside a transaction (`db.transaction`),
 * so `waitForRowPresent` has no direct caller in `courses/` today,
 * but is included anyway: it is half the canonical contract and
 * `saveLessonProgressMutation` (existing SELECT → manual UPDATE or INSERT, see
 * `courses/mutations.ts`) is exactly the kind of compound operation it was
 * designed for.
 *
 * ── What this module does NOT cover (deliberately) ───────────────────────────
 * Hard `DELETE`s (there are none in `courses/`; course, unit, lesson, block,
 * and enrollment deletes are soft-delete with `deleted_at`, so they DO pass
 * through here as a normal patch) and read-only server-authoritative tables
 * (`learner_stats`, `leaderboard_entry`, `certificate`: the client never writes
 * them locally, they only arrive through sync) are out of scope for the same
 * reason as in the canon: the natural check for a deletion or a row
 * the client never created is "the row is gone" / "I never put it there",
 * which is exactly the ambiguous state of this window.
 */

/**
 * Retry backoff. Sums to ≈ 6.05 s: more than covers a checkpoint round trip
 * without leaving a quick interaction hanging forever if the row really does
 * not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
}

/** Error for a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if it is not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read
  // uses the primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- generic over SQLiteTable; the real shape is only known at runtime
  const rows = (await db
    .select()
    .from(table)
    .where(eq(idColumn, id))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" value when compared with SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * `UPDATE … WHERE id = ?` **verified**: writes, reads back, compares the
 * patch fields, and retries while they have not landed. Throws
 * `LocalWriteLostError` if they are still absent after the backoff.
 *
 * `updated_at` is set by this function on every attempt (and is not compared:
 * it changes each pass); the `patch` must contain only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(eq(idColumn, id));

    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits until the row EXISTS in the local view before touching it.
 *
 * For mutations that are not a simple patch —a compound operation where
 * retrying inside the transaction is pointless (it would sleep while holding
 * the write lock), and where operating on a missing row is worse than losing
 * a field: an UPDATE that matches nothing while the sibling INSERT succeeds
 * leaves orphaned or duplicate data.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}
